// Ultimate Card Game (UCG) / VOID Card Warfare - Closed Alpha Access Gate
//
// Restricts game access during the Friends & Family test phase (5-15 players).
// Checks for:
// 1. Persistent access flag in localStorage ('void_alpha_access' === 'granted')
// 2. URL parameter ?alpha=KEY or ?access=KEY (e.g. ?alpha=VOID-A7X9-K2M4)
// 3. Manual code entry modal if neither is present
// 4. One-shot dynamic server keys (via api/claim_alpha.php) with bonus packages & pre-set call-signs.

import { wallet } from '../engine/wallet.js';

export const VALID_ALPHA_KEYS = new Set([
  'voidconflict',
  'voidconflict2026',
  'deckinvaders2026',
  'deckinvaders',
  'void2026',
  'alpha2026',
  'voidalpha',
  'commander2026'
]);

export const STORAGE_KEY_ALPHA = 'void_alpha_access';

export function isKeyValid(key) {
  if (!key || typeof key !== 'string') return false;
  return VALID_ALPHA_KEYS.has(key.trim().toLowerCase());
}

export function grantAlphaAccess() {
  try {
    localStorage.setItem(STORAGE_KEY_ALPHA, 'granted');
    return true;
  } catch {
    return false;
  }
}

export function hasAlphaAccess() {
  try {
    if (localStorage.getItem(STORAGE_KEY_ALPHA) === 'granted') return true;
    const hash = window.location?.hash ? window.location.hash.trim() : '';
    if (/^#a=[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/i.test(hash)) return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get('dare') === '1') {
      grantAlphaAccess();
      return true;
    }
    const candidate = params.get('alpha') || params.get('access');
    if (candidate && isKeyValid(candidate)) return true;
  } catch {}
  return false;
}

function cleanUrlParams() {
  try {
    if (typeof window === 'undefined' || !window.location) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('alpha') && !params.has('access')) return;
    params.delete('alpha');
    params.delete('access');
    const newQuery = params.toString() ? `?${params.toString()}` : '';
    window.history.replaceState(null, '', window.location.pathname + newQuery + (window.location.hash || ''));
  } catch {}
}

export async function claimAlphaKey(key, auth = null) {
  if (!key || typeof key !== 'string') {
    return { ok: false, error: 'missing_key', message: 'Clearance key cannot be empty.' };
  }

  const cleanKey = key.trim();
  if (isKeyValid(cleanKey)) {
    grantAlphaAccess();
    return { ok: true, source: 'static' };
  }

  try {
    let currentAuth = auth;
    if (!currentAuth) {
      try {
        const { getAuth, ensureIdentity } = await import('../net/playerIdentity.js');
        currentAuth = getAuth();
        if (!currentAuth?.accountId) {
          currentAuth = await ensureIdentity();
        }
      } catch {}
    }

    const payload = { key: cleanKey };
    if (currentAuth?.accountId && currentAuth?.secret) {
      payload.accountId = currentAuth.accountId;
      payload.secret = currentAuth.secret;
    }

    const res = await fetch('api/claim_alpha.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => null);

    if (res.ok && data?.ok) {
      grantAlphaAccess();

      // Apply predefined Call-Sign if set on the key
      if (data.predefinedName) {
        try {
          // Immediately set in localStorage so getDisplayName() returns it synchronously
          localStorage.setItem('ucg_player_name', data.predefinedName);
          const { setDisplayName, initAuthFromStorage } = await import('../net/playerIdentity.js');
          initAuthFromStorage();
          await setDisplayName(data.predefinedName);
        } catch {
          try {
            localStorage.setItem('ucg_player_name', data.predefinedName);
          } catch {}
        }
      }

      // Apply starter bonus package
      if (data.bonusPackage === 'vip') {
        wallet.addFugazi(500, 'alpha_bonus');
        wallet.addTickets(5, 'alpha_bonus');
      } else if (data.bonusPackage === 'fleet') {
        wallet.addFugazi(1000, 'alpha_bonus');
        wallet.addTickets(10, 'alpha_bonus');
      }

      return { ok: true, data };
    }

    return {
      ok: false,
      error: data?.error || 'claim_failed',
      message: data?.message || 'Clearance Denied // Invalid Authorization Code'
    };
  } catch (err) {
    return {
      ok: false,
      error: 'network_error',
      message: 'Clearance verification failed (network error).'
    };
  }
}

export function checkAlphaAccess(options = { prompt: true }) {
  const prompt = options?.prompt !== false;

  // 1. ALWAYS check URL search parameters FIRST (?alpha=... or ?access=...)!
  // If the user arrives with a key in the URL, they explicitly want to redeem it,
  // regardless of whether the device already had access granted previously.
  try {
    if (typeof window !== 'undefined' && window.location) {
      const params = new URLSearchParams(window.location.search);
      // Tactical Dare invite bypass: grants immediate clearance without prompting
      if (params.get('dare') === '1') {
        grantAlphaAccess();
        return Promise.resolve(true);
      }
      const candidate = params.get('alpha') || params.get('access');
      if (candidate) {
        if (isKeyValid(candidate)) {
          grantAlphaAccess();
          cleanUrlParams();
          return Promise.resolve(true);
        }
        // Dynamic one-shot clearance key from URL
        return claimAlphaKey(candidate).then((result) => {
          cleanUrlParams();
          if (result.ok) {
            return true;
          }
          if (prompt) {
            return renderAlphaModal(result.message);
          }
          return false;
        });
      }
    }
  } catch {
    // Ignore URL parse errors
  }

  // 2. Check if device is already granted (only if no new key was provided in URL)
  try {
    if (localStorage.getItem(STORAGE_KEY_ALPHA) === 'granted') {
      return Promise.resolve(true);
    }
  } catch {
    // localStorage restricted - fallback to modal
  }

  // 3. Check if player arrives via valid Account Recovery link (format: #a=XXXX-XXXX-XXXX)
  try {
    const hash = window.location?.hash ? window.location.hash.trim() : '';
    if (/^#a=[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/i.test(hash)) {
      grantAlphaAccess();
      return Promise.resolve(true);
    }
  } catch {
    // Ignore location parse errors
  }

  if (!prompt) {
    return Promise.resolve(false);
  }

  // 4. Render Access Modal & wait for user input
  return renderAlphaModal();
}

function renderAlphaModal(initialError = null) {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      return resolve(false);
    }
    const layer = document.createElement('div');
    layer.className = 'fullscreen-dialog-overlay active alpha-gate-fullscreen';
    layer.id = 'alpha-gate-overlay';
    layer.style.zIndex = '99999';

    layer.innerHTML = `
      <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
        <div class="fullscreen-dialog-bg-image"></div>
        <div class="fullscreen-dialog-scrim"></div>
        <div class="fullscreen-dialog-vignette"></div>
      </div>
      <div class="fullscreen-dialog-container" style="justify-content: center; align-items: center; min-height: 100vh;">
        <div class="fullscreen-card-panel" style="max-width: 480px; width: 92%; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 16px; padding: clamp(28px, 4vh, 40px) clamp(22px, 3.5vw, 36px); position: relative;">
          <button type="button" id="alpha-gate-close" title="Cancel (Esc)" aria-label="Cancel" style="position: absolute; top: 16px; right: 16px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.15); color: #94a3b8; border-radius: 6px; width: 32px; height: 32px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 16px; transition: all 0.2s;">✕</button>
          <div class="fullscreen-dialog-status-pill status-pill-circular status-red" style="margin-bottom: 2px;">
            <span class="status-indicator-dot" style="background: #ef4444; box-shadow: 0 0 8px #ef4444;"></span>
          </div>
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div class="account-overlay-title" style="font-family: 'Orbitron', sans-serif; font-size: clamp(20px, 2.5vw, 24px); font-weight: 900; letter-spacing: 2px; color: #00f0ff; text-shadow: 0 0 16px rgba(0, 240, 255, 0.45); margin: 0;">
              RESTRICTED CLEARANCE
            </div>
            <div style="font-family: 'Rajdhani', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 2.5px; color: rgba(148, 163, 184, 0.8); text-transform: uppercase;">
              // CLOSED ALPHA ACCESS
            </div>
          </div>
          <div class="account-overlay-text" style="font-family: 'Rajdhani', sans-serif; font-size: clamp(15px, 1.8vw, 17px); font-weight: 600; line-height: 1.5; color: #cbd5e1; margin-top: 4px; margin-bottom: 4px;">
            Access to the <strong style="color: #f8fafc;">VOID Conflict</strong> operational zone is currently restricted to authorized test Commanders.
            <br><br>
            Please enter your invitation clearance code:
          </div>
          <input type="text" id="alpha-gate-input" class="account-overlay-input" placeholder="ACCESS-CODE" autocomplete="off" spellcheck="false" maxlength="32" style="width: 100%; height: 50px; font-family: 'IBM Plex Mono', monospace; font-size: 18px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; text-align: center; border-radius: 8px; border: 1.5px solid rgba(0, 240, 255, 0.4); background: rgba(0, 240, 255, 0.08); color: #00f0ff; box-sizing: border-box; outline: none; box-shadow: 0 0 14px rgba(0, 240, 255, 0.2); transition: border-color 0.2s, box-shadow 0.2s;" />
          <div id="alpha-gate-error" class="account-overlay-status account-overlay-status-error" style="display: ${initialError ? 'block' : 'none'}; font-family: 'Rajdhani', sans-serif; font-size: 14px; font-weight: 700; color: #f87171; letter-spacing: 1.2px; text-transform: uppercase;">
            ${initialError || 'Clearance Denied // Invalid Authorization Code'}
          </div>
          <button type="button" id="alpha-gate-submit" class="start-btn start-btn-primary" style="width: 100%; height: 50px; margin-top: 6px;">
            <span class="start-btn-text">AUTHORIZE ACCESS</span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(layer);

    const input = layer.querySelector('#alpha-gate-input');
    const errorEl = layer.querySelector('#alpha-gate-error');
    const submitBtn = layer.querySelector('#alpha-gate-submit');
    const closeBtn = layer.querySelector('#alpha-gate-close');

    // Auto-focus input
    setTimeout(() => { input?.focus(); }, 150);

    let closed = false;
    const finish = (result) => {
      if (closed) return;
      closed = true;
      window.removeEventListener('keydown', handleKey);
      layer.style.transition = 'opacity 0.25s ease';
      layer.style.opacity = '0';
      setTimeout(() => {
        layer.remove();
        resolve(result);
      }, 250);
    };

    const handleKey = (e) => {
      if (e.key === 'Escape') finish(false);
    };
    window.addEventListener('keydown', handleKey);
    closeBtn?.addEventListener('click', () => finish(false));
    layer.addEventListener('click', (e) => {
      if (e.target.classList.contains('fullscreen-dialog-container')) {
        finish(false);
      }
    });

    const trySubmit = async () => {
      const code = input?.value || '';
      if (!code.trim()) return;

      if (isKeyValid(code)) {
        grantAlphaAccess();
        finish(true);
        return;
      }

      // Check dynamic one-shot key
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="start-btn-text">VERIFYING CLEARANCE...</span>';
      if (errorEl) errorEl.style.display = 'none';

      const result = await claimAlphaKey(code);
      if (result.ok) {
        submitBtn.innerHTML = '<span class="start-btn-text" style="color: #4ade80;">CLEARANCE GRANTED // WELCOME</span>';
        setTimeout(() => finish(true), 350);
      } else {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<span class="start-btn-text">AUTHORIZE ACCESS</span>';
        if (errorEl) {
          errorEl.textContent = result.message || 'Clearance Denied // Invalid Authorization Code';
          errorEl.style.display = 'block';
        }
        if (input) {
          input.style.borderColor = '#ff4455';
          input.select();
        }
      }
    };

    submitBtn?.addEventListener('click', trySubmit);
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') trySubmit();
      else if (errorEl) errorEl.style.display = 'none';
      if (input) input.style.borderColor = '';
    });
  });
}
